import { Controller } from "@hotwired/stimulus"

// Builds an "On this page" rail from the `<h2>` headings already in the document, and
// highlights whichever section the reader is on (a scrollspy).
//
// Deliberately derived from the DOM rather than a list in Ruby: a page gains an entry just by
// adding a `<section id>` with a heading, and the rail picks it up with nothing else to update.
// Shared by the styleguide (layouts/styleguide.html.erb) and merchant help guides
// (views/help/*). Mount it on the rail element and give it a `[data-doc-toc-target="list"]`
// nav to fill; it reads the section headings from `main section[id]` on the page.
//
// The active entry is the last section whose top has crossed a trigger line a little below the
// sticky header — a position-based rule (rather than raw IntersectionObserver "is it visible")
// that stays correct whichever section is short, tall, jumped-to, or scrolled past, and works the
// same whether the page scrolls the window (styleguide) or an inner overflow container (the app).
export default class extends Controller {
  static targets = ["list"]

  // Which sections to index. Defaults to the page's `main` (styleguide / full-page help); the help
  // drawer scopes it to its turbo-frame so it indexes the loaded guide.
  static values = { selector: { type: String, default: "main section[id]" } }

  // Distance below the top of the scrollport at which a section becomes "current". Clears the
  // sticky page header on both layouts.
  static TRIGGER = 120

  // Class strings are written out in full so Tailwind's JIT scanner finds them; it reads
  // app/javascript/**/*.js as source, but only as text, so a built-up string would not compile.
  static IDLE =
    "block truncate rounded-md px-3 py-1.5 text-[13px] text-gray-500 hover:bg-gray-50 hover:text-gray-900"
  static ACTIVE =
    "block truncate rounded-md px-3 py-1.5 text-[13px] font-semibold bg-gray-100 text-gray-900"

  connect() {
    // The help drawer's sections arrive via a turbo-frame load after connect(), so (re)build then too.
    this.rebuild = () => this.build()
    this.build()
    document.addEventListener("turbo:frame-load", this.rebuild)
  }

  disconnect() {
    document.removeEventListener("turbo:frame-load", this.rebuild)
    this.removeScrollListeners()
  }

  build() {
    this.removeScrollListeners()
    // Turbo renders the cached snapshot of a page before the fresh body arrives, so this can run
    // twice on a navigation — and the second run would append a second copy of every link on top of
    // the first. Starting from empty makes the build idempotent.
    this.listTarget.replaceChildren()
    this.activeIndex = undefined

    const sections = Array.from(document.querySelectorAll(this.selectorValue))

    // One entry needs no index, and neither does a section built from prose rather than examples.
    if (sections.length < 2) {
      this.element.hidden = true
      return
    }
    this.element.hidden = false

    this.entries = []

    sections.forEach((section) => {
      const heading = section.querySelector("h2")
      if (!heading) return

      const link = document.createElement("a")
      link.href = `#${section.id}`
      // The heading's first child is its text; the rest is the API chip, which would make
      // every rail entry twice as long for no gain.
      link.textContent = (heading.firstChild?.textContent || heading.textContent).trim()
      link.className = this.constructor.IDLE
      // Scroll programmatically instead of navigating to the anchor: inside the help drawer a real
      // `#id` navigation is intercepted by Turbo and tears the drawer down. scrollIntoView honours the
      // sections' scroll-margin and scrolls whichever container (window or the drawer) is scrolling.
      link.addEventListener("click", (event) => {
        event.preventDefault()
        section.scrollIntoView({ behavior: "smooth", block: "start" })
      })
      this.listTarget.appendChild(link)
      this.entries.push({ section, link })
    })

    this.scroller = this.scrollParent(sections[0])
    this.scrollTarget = this.scroller === document.scrollingElement ? window : this.scroller

    // rAF-coalesce the scroll handler: many scroll events per frame, one measurement per frame.
    this.onScroll = () => {
      if (this.ticking) return
      this.ticking = true
      requestAnimationFrame(() => {
        this.ticking = false
        this.render()
      })
    }
    this.scrollTarget.addEventListener("scroll", this.onScroll, { passive: true })
    window.addEventListener("resize", this.onScroll, { passive: true })

    this.render()
  }

  removeScrollListeners() {
    this.scrollTarget?.removeEventListener("scroll", this.onScroll)
    window.removeEventListener("resize", this.onScroll)
  }

  render() {
    // Once the scroller bottoms out, a short final section can never cross the trigger line, so
    // pin it — otherwise it would never light up.
    if (this.atBottom()) return this.setActive(this.entries.length - 1)

    const trigger = this.scrollTop() + this.constructor.TRIGGER
    let active = 0
    this.entries.forEach(({ section }, index) => {
      if (this.offsetTop(section) <= trigger) active = index
    })
    this.setActive(active)
  }

  setActive(index) {
    if (index === this.activeIndex) return
    this.activeIndex = index

    this.entries.forEach(({ link }, i) => {
      link.className = i === index ? this.constructor.ACTIVE : this.constructor.IDLE
    })
    // Long indexes scroll the active entry out of sight in the rail's own overflow.
    this.entries[index].link.scrollIntoView({ block: "nearest" })
  }

  // Scroll offset and section position, both measured against the scroller's own coordinate space
  // so the maths is identical for window- and container-scrolled layouts.
  scrollTop() {
    return this.scroller === document.scrollingElement ? window.scrollY : this.scroller.scrollTop
  }

  offsetTop(section) {
    if (this.scroller === document.scrollingElement) {
      return section.getBoundingClientRect().top + window.scrollY
    }
    return section.getBoundingClientRect().top - this.scroller.getBoundingClientRect().top + this.scroller.scrollTop
  }

  atBottom() {
    const s = this.scroller
    const viewport = s === document.scrollingElement ? window.innerHeight : s.clientHeight
    return this.scrollTop() + viewport >= s.scrollHeight - 2
  }

  // The nearest ancestor that actually scrolls (an overflow container in the app layout, or the
  // document in the styleguide's full-page layout).
  scrollParent(el) {
    for (let node = el.parentElement; node; node = node.parentElement) {
      const overflowY = getComputedStyle(node).overflowY
      if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) {
        return node
      }
    }
    return document.scrollingElement
  }
}
